import { useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";

import { canonicalUrl, seoPage } from "./seoPages";
import { useTrials } from "./trials";

function setTag(selector: string, create: () => HTMLElement, attr: string, value: string) {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

const meta = (attr: "name" | "property", key: string) => () => {
  const el = document.createElement("meta");
  el.setAttribute(attr, key);
  return el;
};

/**
 * Keeps a public page's head (title, description, canonical, Open Graph) the
 * same as its static HTML after client-side navigation (src/site/seoPages.ts).
 * Mounted at the router's root; its effect runs after the page's own, so the
 * route table wins (the public pages in it set no title of their own).
 */
export function SeoHead() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  // The free-trial switches: the static head promises no trial; the browser adds it while one is on.
  const trials = useTrials(seoPage(path) !== undefined); // public pages only
  useEffect(() => {
    const page = seoPage(path, trials);
    if (!page) return;
    const url = canonicalUrl(page.path);
    document.title = page.title;
    setTag('meta[name="description"]', meta("name", "description"), "content", page.description);
    setTag('meta[property="og:title"]', meta("property", "og:title"), "content", page.title);
    setTag('meta[property="og:description"]', meta("property", "og:description"), "content", page.description);
    setTag('meta[property="og:url"]', meta("property", "og:url"), "content", url);
    setTag('meta[name="twitter:title"]', meta("name", "twitter:title"), "content", page.title);
    setTag('meta[name="twitter:description"]', meta("name", "twitter:description"), "content", page.description);
    setTag(
      'link[rel="canonical"]',
      () => {
        const el = document.createElement("link");
        el.rel = "canonical";
        return el;
      },
      "href",
      url,
    );
  });
  return null;
}
